/**
 * SecurityExposure — one security across the whole book, and what a shock does to it (TASK-904).
 *
 * `Across my whole book, what is my total NVIDIA exposure, which clients hold it, and what is
 * the CHF loss if it falls 20%?` was refused honestly on 2026-08-31 — and that was a regression
 * in usefulness, not a fix. The pre-TASK-871 Research Brief carried a `Your book` panel showing
 * **3 clients · CHF 808'409**, split per client, which reconciled with the Portfolio Analytic
 * page. The panel was right; the brief was the wrong container for a portfolio question, so the
 * honest refusal took a correct answer off the screen with it.
 *
 * **Book-level, and that is how the scope leak is fixed.** The refusal was scoped to "Julian
 * Ammann's portfolio and composition tabs" because the client chip leaked from the previous turn
 * (TASK-899). A `_BOOK_LEVEL` widget is never handed a client, so "my whole book" cannot be
 * answered inside whoever the canvas last opened — no new code, just the right registration.
 *
 * **The shock is the RM's own number**, applied to the held value, which is exactly what the
 * book page's `IMPACT IF -10%` already does. Nothing here models a market.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { httpClient } from '../../../lib/httpClient';
import { WidgetErrorState } from '../../ui/WidgetErrorState';
import { WidgetContainer } from '../WidgetContainer';
import { OpenAsSpreadsheet } from './OpenAsSpreadsheet';

const chf = (v: number) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency', currency: 'CHF', maximumFractionDigits: 0,
  }).format(v);

const pct = (v: number | null | undefined) =>
  (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);

export const DEFAULT_SHOCK_PCT = -10;

export interface ExposureClientRow {
  /** The first of `position_ids`. The row is a per-CLIENT aggregate, so it may fold several. */
  position_id: string;
  position_ids?: string[];
  position_count?: number;
  client_id?: string;
  client_name?: string | null;
  isin?: string | null;
  security_name?: string | null;
  exposure_chf: number;
  pct_of_client: number | null;
  loss_chf: number;
}

export interface SecurityExposureResponse {
  subject: string;
  resolved_name?: string | null;
  resolved_isins?: string[];
  ambiguous?: boolean;
  clients: ExposureClientRow[];
  client_count: number;
  shock_pct: number;
  /** Null for a viewer who cannot see the whole book (TASK-906's rule). */
  book_exposure_chf: number | null;
  book_loss_chf: number | null;
  book_scope_restricted?: boolean;
  unpriceable_currencies?: string[];
  truncated?: boolean;
  /** The RM gave too little to identify a security — one letter matches most of a book. */
  subject_too_broad?: boolean;
  timestamp?: string | null;
}

export interface SecurityExposureProps {
  data: SecurityExposureResponse | null;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function SecurityExposure({
  data, isLoading = false, isError = false, error = null, onRetry,
}: SecurityExposureProps) {
  const shock = data?.shock_pct ?? DEFAULT_SHOCK_PCT;
  const shockLabel = `${shock > 0 ? '-' : ''}${shock}%`;

  const body = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 p-6 text-xs text-muted-foreground"
             data-testid="exposure-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Reading the book…
        </div>
      );
    }

    if (isError) {
      return (
        <WidgetErrorState
          title="Couldn't read that security across the book"
          detail={error?.message}
          onRetry={onRetry}
        />
      );
    }

    if (data?.subject_too_broad) {
      return (
        <p className="p-6 text-xs text-muted-foreground" data-testid="exposure-too-broad">
          &ldquo;{data.subject}&rdquo; matches too much to be a security. Name it more fully.
        </p>
      );
    }

    const rows = data?.clients ?? [];

    if (rows.length === 0) {
      // "No client holds it" is an ANSWER. It must not read like a failure, and it must name
      // what was searched for so the RM can see whether the name was understood.
      return (
        <p className="p-6 text-xs text-muted-foreground" data-testid="exposure-empty">
          No client in your book holds {data?.resolved_name ?? data?.subject ?? 'that security'}.
        </p>
      );
    }

    return (
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 pb-1"
             data-testid="exposure-header">
          <p className="text-xs font-medium">
            {data?.resolved_name ?? data?.subject}
            {data?.resolved_isins?.length === 1 && (
              <span className="ml-2 font-mono text-[10px] text-muted-foreground">
                {data.resolved_isins[0]}
              </span>
            )}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {data?.book_exposure_chf == null
              ? 'book exposure not shown for your client scope'
              : `book exposure ${chf(data.book_exposure_chf)}`}
            {' · '}{data?.client_count ?? rows.length} client
            {(data?.client_count ?? rows.length) === 1 ? '' : 's'}
          </p>
        </div>

        <div className="flex flex-wrap items-baseline justify-between gap-2 pb-2"
             data-testid="exposure-shock">
          <p className="text-xs">shock {shockLabel}</p>
          <p className="text-[10px] text-muted-foreground">
            {data?.book_loss_chf == null
              ? 'book loss not shown for your client scope'
              : `book loss ${chf(data.book_loss_chf)}`}
          </p>
        </div>

        {data?.ambiguous && (
          // Several ISINs matched the name. Saying which, rather than silently summing them
          // under one heading, is the difference between an aggregate and a claim.
          <p className="pb-2 text-[10px] text-muted-foreground" data-testid="exposure-ambiguous">
            Matched {data.resolved_isins?.length} instruments: {data.resolved_isins?.join(', ')}.
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="exposure-table">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-1 text-left font-normal">client</th>
                <th className="py-1 text-right font-normal">exposure</th>
                <th className="py-1 text-right font-normal">% of portfolio</th>
                <th className="py-1 text-right font-normal">loss at {shockLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.position_id}
                  data-testid="exposure-row"
                  data-position-id={r.position_id}
                  className="border-b border-border/40 last:border-0"
                >
                  <td className="max-w-[200px] truncate py-1">
                    {r.client_name ?? '—'}
                    {(r.position_count ?? 1) > 1 && (
                      // The row sums several lines for this client — said, not implied, because
                      // the per-position version of this table showed one client three times.
                      <span className="ml-1 text-[10px] text-muted-foreground">
                        ({r.position_count} positions)
                      </span>
                    )}
                  </td>
                  <td className="py-1 text-right tabular-nums">{chf(r.exposure_chf)}</td>
                  <td className="py-1 text-right tabular-nums">{pct(r.pct_of_client)}</td>
                  <td className="py-1 text-right tabular-nums">{chf(r.loss_chf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="pt-2 text-[10px] text-muted-foreground" data-testid="exposure-footer">
          The shock is applied to the held value at {shockLabel}; it is not a market forecast.
          {' '}% of portfolio is against each client&apos;s whole portfolio, cash included.
          {(data?.unpriceable_currencies?.length ?? 0) > 0 && (
            ` Positions in ${data!.unpriceable_currencies!.join(', ')} have no CHF rate and are `
            + 'excluded.'
          )}
        </p>
      </div>
    );
  };

  return (
    <WidgetContainer
      source="Portfolio · Security exposure"
      dataAsOf={data?.timestamp ?? null}
      toolbar={
        (data?.clients?.length ?? 0) > 0 ? (
          <OpenAsSpreadsheet
            sourceWidget="SecurityExposure"
            asOf={data?.timestamp ?? null}
            build={() => ({
              title: `${data?.resolved_name ?? data?.subject} across the book (shock ${shockLabel})`,
              sheetName: 'Exposure',
              columns: [
                { header: 'Client', width: 26 },
                { header: 'ISIN', width: 16 },
                { header: 'Exposure CHF', fmt: 'currency', width: 16 },
                { header: '% of portfolio', fmt: 'percent', width: 14 },
                { header: `Loss at ${shockLabel}`, fmt: 'currency', width: 16 },
                { header: 'position_id', width: 38 },
              ],
              rows: (data?.clients ?? []).map((r) => [
                r.client_name ?? '',
                r.isin ?? '',
                r.exposure_chf,
                r.pct_of_client === null ? null : r.pct_of_client / 100,
                r.loss_chf,
                r.position_id,
              ]),
            })}
          />
        ) : undefined
      }
    >
      {body()}
    </WidgetContainer>
  );
}

export interface SecurityExposurePanelProps {
  filters?: { subject?: string; shock?: number };
  subject?: string;
  shock?: number;
}

export function SecurityExposurePanel({ filters, subject, shock }: SecurityExposurePanelProps) {
  const effectiveSubject = filters?.subject ?? subject ?? '';
  const effectiveShock = filters?.shock ?? shock ?? DEFAULT_SHOCK_PCT;

  const query = useQuery({
    queryKey: ['security-exposure', effectiveSubject, effectiveShock],
    queryFn: () => httpClient.get<SecurityExposureResponse>(
      `/api/portfolio/my-book/security-exposure?subject=${encodeURIComponent(effectiveSubject)}`
      + `&shock=${effectiveShock}`,
    ),
    enabled: effectiveSubject.trim().length > 0,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // Without a security there is nothing to read. Said plainly rather than rendered as an empty
  // book — a disabled query is not `isLoading`, so it would otherwise fall through to "no
  // client holds it", asserting an absence never checked.
  if (!effectiveSubject.trim()) {
    return (
      <WidgetContainer source="Portfolio · Security exposure">
        <p className="p-6 text-xs text-muted-foreground" data-testid="exposure-no-subject">
          Name a security and I&apos;ll show who holds it across your book.
        </p>
      </WidgetContainer>
    );
  }

  return (
    <SecurityExposure
      data={query.data ?? null}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error as Error | null}
      onRetry={() => void query.refetch()}
    />
  );
}

export default SecurityExposurePanel;
